import { SensorConversions } from "../conversionFunctions/sensorConversions.js";
import { sensorParameterNumericValidators } from "../SensorConfigurationParameterHelpers.js";
import { SensorParameterConfigBase, SensorParameterValueType } from "../SensorConfigurationTypes.js";
import { SensorElementConfig } from "./types.js";

const SECONDS_PER_MINUTE = 60;

const flowmeterParams = {
    kFactor: {
        key: "kFactor",
        type: SensorParameterValueType.NUMBER,
        name: "K-factor",
        description: "Volume per pulse (liters per pulse). Converts pulse frequency to flow rate.",
        defaultValue: 1,
        validator: sensorParameterNumericValidators.isGreaterThanZero,
        mustBeNonNegative: true,
        mustBeInteger: false,
        parameterUnitCategory: "flowmeterKFactor",
    },
    minimumFrequency: {
        key: "minimumFrequency",
        type: SensorParameterValueType.NUMBER,
        name: "Minimum frequency",
        description: "Pulse frequency at or below which the flow reading is forced to zero (Hz).",
        defaultValue: 0,
        validator: sensorParameterNumericValidators.isGreaterThanOrEqualToZero,
        mustBeNonNegative: true,
        mustBeInteger: false,
        parameterUnitCategory: "flowmeterMinimumFrequency",
    },
    frequencyOffset: {
        key: "frequencyOffset",
        type: SensorParameterValueType.NUMBER,
        name: "Frequency offset",
        description:
            "Offset added to the raw pulse frequency before applying the K-factor (Hz). Used for sensors with a non-zero intercept in their calibration curve.",
        defaultValue: 0,
        validator: sensorParameterNumericValidators.isNumber,
        mustBeNonNegative: false,
        mustBeInteger: false,
        parameterUnitCategory: "flowmeterOffset",
    },
} as const satisfies Record<string, SensorParameterConfigBase<number>>;

export const flowmeterElementConfig: SensorElementConfig<
    typeof flowmeterParams,
    typeof SensorConversions.linearWithZeroBeforeThreshold
> = {
    legalConvertedRange: [0, null],
    conversionKey: "linearWithZeroBeforeThreshold",
    parameterConfigs: flowmeterParams,
    buildConversionParams: ({ kFactor, minimumFrequency, frequencyOffset }) => ({
        slope: kFactor * SECONDS_PER_MINUTE,
        offset: frequencyOffset * kFactor * SECONDS_PER_MINUTE,
        zeroThreshold: minimumFrequency,
        outputMin: 0,
        outputMax: Number.MAX_VALUE,
    }),
    decodedUnits: "hertz",
    convertedUnits: "liters_per_minute",
    unitCategory: "liquidFlow",
    testCases: [
        {
            // Normal flow: kFactor=2 L/pulse, minimumFrequency=0.5 Hz, no offset → slope=120, 10 Hz → 1200 L/min
            parameters: { kFactor: 2, minimumFrequency: 0.5, frequencyOffset: 0 },
            decodedValue: 10,
            convertedValue: 1200,
        },
        {
            // Frequency offset: kFactor=2, offset=1 Hz → result = (10+1)*120 = 1320 L/min
            parameters: { kFactor: 2, minimumFrequency: 0.5, frequencyOffset: 1 },
            decodedValue: 10,
            convertedValue: 1320,
        },
        {
            // At threshold: flow is forced to zero; reverse skipped because inverse of 0 is ambiguous
            parameters: { kFactor: 2, minimumFrequency: 0.5, frequencyOffset: 0 },
            decodedValue: 0.5,
            convertedValue: 0,
            skipBackwardConversionCheck: true,
        },
    ],
} as const;
